import React, { useMemo } from 'react';
import moment from 'moment-timezone';

import {
	TextField,
	Typography,
	Grid,
	Radio,
	RadioGroup,
	FormLabel,
	FormControl,
	FormControlLabel,
	Box,
} from '@mui/material';
import ContainerBox from 'components/ContainerBox';

import { CurrencyObject, DateFormatObject, UnitSystemType, ShopPublicInfo } from 'common/types';
import {
	currencyOptions as currencies,
	getCurrencyInfoFromCurrencyCode,
	getCurrencySymbolFromCurrencyCode,
	getDefaultCurrencyObject,
} from 'common/utils/currencyUtils';
import {
	DATE_FORMATS,
	TIME_FORMATS,
	START_DAYS,
	getDefaultDateObject,
} from 'common/utils/dateUtils';
import { useTranslation } from 'services/localization';
import { getUnitSystemOptions } from 'common/utils/userDetails';
import { getDayTypeTranslation } from 'common/utils/translations';

interface OwnProps {
	publicInfo: ShopPublicInfo;
	setPublicInfo: (publicInfo: ShopPublicInfo) => void;
}

const dateFormatOptions = DATE_FORMATS.map((dateFormat) => (
	<option key={dateFormat} value={dateFormat}>
		{moment().format(dateFormat)}
	</option>
));

const timeFormatOptions = TIME_FORMATS.map((time) => (
	<option key={time} value={time}>
		{moment('21:00', 'HH:mm').format(time)}
	</option>
));

type Props = OwnProps;

const LocalizationSettings = ({ publicInfo, setPublicInfo }: Props) => {
	const { t } = useTranslation();

	const unitSystem: UnitSystemType = publicInfo.unitSystem;
	const currencyObject: CurrencyObject = publicInfo.currency ?? getDefaultCurrencyObject();
	const dateFormatObject: DateFormatObject = publicInfo.dateFormat ?? getDefaultDateObject();
	const currencyInfo = getCurrencyInfoFromCurrencyCode(currencyObject.code);

	const startDayOptions = START_DAYS.map((day) => {
		return (
			<option key={day} value={day}>
				{getDayTypeTranslation(day, t)}
			</option>
		);
	});
	const currencyOptions = useMemo(
		() =>
			currencies.map((currency) => (
				<option key={currency.code} value={currency.code}>
					{`${currency.code} (${currency.name})`}
				</option>
			)),
		[],
	);

	const displayAsOptions = useMemo(
		() =>
			!!currencyInfo
				? [...new Set([currencyInfo.code, ...currencyInfo.symbols])].map((displayAs) => (
						<option key={displayAs} value={displayAs}>
							{displayAs}
						</option>
				  ))
				: [currencyObject.code],
		[currencyInfo, currencyObject],
	);

	const unitSystemOptions = useMemo(
		() =>
			getUnitSystemOptions(t).map((unit) => (
				<option key={unit.value} value={unit.value}>
					{unit.label}
				</option>
			)),
		[t],
	);

	const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const updatedCurrency = {
			...currencyObject,
			[name]: value,
			...(name === 'code' && {
				displayAs:
					currencyObject.displayAs === currencyObject.code
						? value
						: getCurrencySymbolFromCurrencyCode(value),
			}),
		};
		setPublicInfo({ ...publicInfo, currency: updatedCurrency });
	};

	const handleDateFormatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const updatedDateFormat = {
			...dateFormatObject,
			[name]: value,
		};
		setPublicInfo({ ...publicInfo, dateFormat: updatedDateFormat });
	};

	const handleUnitSystemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPublicInfo({ ...publicInfo, unitSystem: e.target.value as UnitSystemType });
	};

	const renderDateAndTime = () => (
		<Box mt={4}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Typography variant="h6" gutterBottom>
						{t('common:times.dateAndTime', 'Date and time')}
					</Typography>
				</Grid>
				<Grid item xs={12} md={6}>
					<TextField
						variant="standard"
						select
						name="dateFormat"
						value={dateFormatObject.dateFormat}
						label={t('shop.dateFormat', 'Date format')}
						onChange={handleDateFormatChange}
						SelectProps={{
							native: true,
						}}
						fullWidth
					>
						{dateFormatOptions}
					</TextField>
					<Box mt={4} />
					<TextField
						variant="standard"
						select
						name="timeFormat"
						value={dateFormatObject.timeFormat}
						label={t('shop.timeFormat', 'Time format')}
						onChange={handleDateFormatChange}
						SelectProps={{
							native: true,
						}}
						fullWidth
					>
						{timeFormatOptions}
					</TextField>
					<Box mt={4} />
					<TextField
						variant="standard"
						select
						name="startDay"
						value={dateFormatObject.startDay}
						label={t('shop.firstDayOfWeek', 'First day of the week')}
						onChange={handleDateFormatChange}
						SelectProps={{
							native: true,
						}}
						fullWidth
					>
						{startDayOptions}
					</TextField>
				</Grid>
			</Grid>
		</Box>
	);

	const renderCurrency = () => (
		<Grid container spacing={2}>
			<Grid item xs={12} md={6}>
				<Typography variant="h6" gutterBottom>
					{t('common:payment.currency', 'Currency')}
				</Typography>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					variant="standard"
					select
					name="code"
					value={currencyObject.code}
					label={t('shop.selectedCurrency', 'Selected currency')}
					onChange={handleCurrencyChange}
					SelectProps={{
						native: true,
					}}
					fullWidth
				>
					{currencyOptions}
				</TextField>
				<Box mt={4} />
				<TextField
					variant="standard"
					select
					name="displayAs"
					value={currencyObject.displayAs}
					label={t('shop.displayCurrencyAs', 'Display currency as')}
					onChange={handleCurrencyChange}
					SelectProps={{
						native: true,
					}}
					fullWidth
				>
					{displayAsOptions}
				</TextField>
				<Box mt={4} />
				<FormControl>
					<FormLabel>
						<Typography variant="caption">
							{t('shop.currencyPosition', 'Currency symbol position')}
						</Typography>
					</FormLabel>
					<RadioGroup
						aria-label={t('currencyPosition', 'Currency symbol position')}
						name="position"
						value={currencyObject.position}
						onChange={handleCurrencyChange}
					>
						<FormControlLabel
							value="prefix"
							control={<Radio color="primary" />}
							label={`${currencyObject.displayAs} 123.00`}
						/>
						<FormControlLabel
							value="suffix"
							control={<Radio color="primary" />}
							label={`123.00 ${currencyObject.displayAs}`}
						/>
					</RadioGroup>
				</FormControl>
			</Grid>
		</Grid>
	);

	const renderMeasurementUnits = () => (
		<Box mt={4}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Typography variant="h6">
						{t('common:units.measurementUnits', 'Measurement units')}
					</Typography>
				</Grid>
				<Grid item xs={12} md={6}>
					<TextField
						variant="standard"
						select
						value={unitSystem}
						label={t('shop.unitSystem', 'Unit system')}
						onChange={handleUnitSystemChange}
						SelectProps={{
							native: true,
						}}
						fullWidth
					>
						{unitSystemOptions}
					</TextField>
				</Grid>
			</Grid>
		</Box>
	);

	return (
		<ContainerBox>
			<Typography variant="h5" gutterBottom>
				{t('shop.localization', 'Localization')}
			</Typography>
			{renderCurrency()}
			{renderDateAndTime()}
			{renderMeasurementUnits()}
		</ContainerBox>
	);
};

export default LocalizationSettings;
